import React, { useMemo, useCallback } from 'react';
import { useEditor, EditorContent, useEditorState } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import {
  FormatBold as FormatBoldIcon,
  FormatItalic as FormatItalicIcon,
  Code as CodeIcon,
  FeaturedPlayList as HeadingIcon,
  FormatListBulleted as BulletListIcon,
  FormatListNumbered as NumberedListIcon,
  Undo as UndoIcon,
  Redo as RedoIcon,
} from '@mui/icons-material';

// Định nghĩa styles là constants để tránh tạo lại
const STYLES = {
  container: { width: '100%' } as const,
  label: {
    display: 'block',
    marginBottom: '8px',
    fontWeight: 600,
    color: '#333333',
    fontSize: '14px',
  } as const,
  editorWrapper: {
    overflow: 'hidden',
    borderRadius: '8px',
    backgroundColor: 'white',
    boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
  } as const,
  editorWrapperError: {
    border: '2px solid #D32F2F',
  } as const,
  editorWrapperNormal: {
    border: '1px solid #E6E7EB',
  } as const,
  toolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    padding: '8px',
    backgroundColor: '#F2F3F5',
    borderBottom: '1px solid #E6E7EB',
    alignItems: 'center',
  } as const,
  editorContent: {
    padding: '16px',
    minHeight: 400,
    maxHeight: 600,
    overflow: 'auto',
    backgroundColor: '#ffffff',
  } as const,
  helperText: {
    marginTop: '4px',
    fontSize: '12px',
  } as const,
  divider: {
    width: '1px',
    height: '24px',
    backgroundColor: '#E6E7EB',
    margin: '0 4px',
  } as const,
};

const getButtonStyle = (isActive: boolean) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '6px 8px',
  fontSize: '14px',
  borderRadius: '4px',
  border: isActive ? 'none' : '1px solid #BDBDBD',
  backgroundColor: isActive ? '#005e5d' : 'white',
  color: isActive ? 'white' : '#333333',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
});

const getDisabledButtonStyle = (isEnabled: boolean) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '6px 8px',
  fontSize: '14px',
  borderRadius: '4px',
  border: '1px solid #BDBDBD',
  backgroundColor: isEnabled ? 'white' : '#F5F5F5',
  color: isEnabled ? '#333333' : '#BDBDBD',
  cursor: isEnabled ? 'pointer' : 'not-allowed',
  transition: 'all 0.2s ease',
});

// Constant className để tránh tạo string mới mỗi render
const EDITOR_CLASS_NAME = 'tiptap-editor';

// Memoized button component để tránh re-render không cần thiết
interface ToolbarButtonProps {
  title: string;
  isActive: boolean;
  onClick: () => void;
  icon?: React.ReactNode;
  children?: React.ReactNode;
}

const ToolbarButton = React.memo<ToolbarButtonProps>(
  ({ title, isActive, onClick, icon, children }) => {
    const [isHovered, setIsHovered] = React.useState(false);

    // Tối ưu: tạo callbacks chỉ một lần
    const handleMouseEnter = React.useCallback(() => setIsHovered(true), []);
    const handleMouseLeave = React.useCallback(() => setIsHovered(false), []);

    const buttonStyle = React.useMemo(() => {
      const base = getButtonStyle(isActive);
      if (!isActive && isHovered) {
        return { ...base, backgroundColor: '#F5F5F5' };
      }
      return base;
    }, [isActive, isHovered]);

    return (
      <button
        title={title}
        onClick={onClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={buttonStyle}
      >
        {icon || children}
      </button>
    );
  }
);

ToolbarButton.displayName = 'ToolbarButton';

interface DisabledToolbarButtonProps {
  title: string;
  onClick: () => void;
  disabled: boolean;
  icon: React.ReactNode;
}

const DisabledToolbarButton = React.memo<DisabledToolbarButtonProps>(
  ({ title, onClick, disabled, icon }) => {
    const [isHovered, setIsHovered] = React.useState(false);

    const handleMouseEnter = React.useCallback(() => {
      if (!disabled) setIsHovered(true);
    }, [disabled]);

    const handleMouseLeave = React.useCallback(() => setIsHovered(false), []);

    const buttonStyle = React.useMemo(() => {
      const base = getDisabledButtonStyle(!disabled);
      if (!disabled && isHovered) {
        return { ...base, backgroundColor: '#F5F5F5' };
      }
      return base;
    }, [disabled, isHovered]);

    return (
      <button
        title={title}
        onClick={onClick}
        disabled={disabled}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={buttonStyle}
      >
        {icon}
      </button>
    );
  }
);

DisabledToolbarButton.displayName = 'DisabledToolbarButton';

// Memoized Toolbar component để tránh re-render toàn bộ toolbar
interface ToolbarProps {
  state: {
    isBold: boolean;
    isItalic: boolean;
    isCode: boolean;
    isCodeBlock: boolean;
    isHeading1: boolean;
    isHeading2: boolean;
    isBulletList: boolean;
    isOrderedList: boolean;
    canUndo: boolean;
    canRedo: boolean;
  };
  onToggleBold: () => void;
  onToggleItalic: () => void;
  onToggleCode: () => void;
  onToggleCodeBlock: () => void;
  onToggleBulletList: () => void;
  onToggleOrderedList: () => void;
  onToggleHeading1: () => void;
  onToggleHeading2: () => void;
  onUndo: () => void;
  onRedo: () => void;
}

const Toolbar = React.memo<ToolbarProps>(({
  state,
  onToggleBold,
  onToggleItalic,
  onToggleCode,
  onToggleCodeBlock,
  onToggleBulletList,
  onToggleOrderedList,
  onToggleHeading1,
  onToggleHeading2,
  onUndo,
  onRedo,
}) => (
  <div style={STYLES.toolbar}>
    <ToolbarButton
      title="Bold"
      isActive={state.isBold}
      onClick={onToggleBold}
      icon={<FormatBoldIcon style={{ fontSize: '18px' }} />}
    />

    <ToolbarButton
      title="Italic"
      isActive={state.isItalic}
      onClick={onToggleItalic}
      icon={<FormatItalicIcon style={{ fontSize: '18px' }} />}
    />

    <div style={STYLES.divider} />

    <ToolbarButton
      title="Code"
      isActive={state.isCode}
      onClick={onToggleCode}
      icon={<CodeIcon style={{ fontSize: '18px' }} />}
    />

    <ToolbarButton
      title="Code Block"
      isActive={state.isCodeBlock}
      onClick={onToggleCodeBlock}
      icon={<CodeIcon style={{ fontSize: '18px' }} />}
    />

    <div style={STYLES.divider} />

    <ToolbarButton
      title="Heading 1"
      isActive={state.isHeading1}
      onClick={onToggleHeading1}
    >
      <span style={{ fontWeight: 600 }}>H1</span>
    </ToolbarButton>

    <ToolbarButton
      title="Heading 2"
      isActive={state.isHeading2}
      onClick={onToggleHeading2}
    >
      <span style={{ fontWeight: 600 }}>H2</span>
    </ToolbarButton>

    <div style={STYLES.divider} />

    <ToolbarButton
      title="Bullet List"
      isActive={state.isBulletList}
      onClick={onToggleBulletList}
      icon={<BulletListIcon style={{ fontSize: '18px' }} />}
    />

    <ToolbarButton
      title="Ordered List"
      isActive={state.isOrderedList}
      onClick={onToggleOrderedList}
      icon={<NumberedListIcon style={{ fontSize: '18px' }} />}
    />

    <div style={STYLES.divider} />

    <DisabledToolbarButton
      title="Undo"
      onClick={onUndo}
      disabled={!state.canUndo}
      icon={<UndoIcon style={{ fontSize: '18px' }} />}
    />

    <DisabledToolbarButton
      title="Redo"
      onClick={onRedo}
      disabled={!state.canRedo}
      icon={<RedoIcon style={{ fontSize: '18px' }} />}
    />
  </div>
));

Toolbar.displayName = 'Toolbar';

export interface RichEditorProps {
  value?: string;
  onChange?: (content: string) => void;
  placeholder?: string;
  error?: boolean;
  helperText?: string;
  label?: string;
  minHeight?: number;
}

export const RichEditor: React.FC<RichEditorProps> = ({
  value = '',
  onChange,
  placeholder = 'Enter your content...',
  error = false,
  helperText = '',
  label = '',
  minHeight = 400,
}) => {
  // Memoize editor config để tránh tạo lại extensions mỗi render
  const editorConfig = useMemo(() => ({
    extensions: [StarterKit],
    content: value,
    onUpdate: ({ editor }: any) => {
      debugger
      onChange?.(editor.getHTML());
    },
  }), [value, onChange]);

  const editor = useEditor(editorConfig);

  React.useEffect(() => {
    debugger
    if (editor && value && editor.getHTML() !== value) {
      editor.commands.setContent(value);
    }
  }, [value, editor]);

  // Tối ưu selector để chỉ track các state cần thiết
  const editorState = useEditorState({
    editor,
    selector: (snapshot) => {
      if (!snapshot.editor) {
        return {
          isBold: false,
          isItalic: false,
          isCode: false,
          isCodeBlock: false,
          isHeading1: false,
          isHeading2: false,
          isBulletList: false,
          isOrderedList: false,
          canUndo: false,
          canRedo: false,
        };
      }
      return {
        isBold: snapshot.editor.isActive('bold'),
        isItalic: snapshot.editor.isActive('italic'),
        isCode: snapshot.editor.isActive('code'),
        isCodeBlock: snapshot.editor.isActive('codeBlock'),
        isHeading1: snapshot.editor.isActive('heading', { level: 1 }),
        isHeading2: snapshot.editor.isActive('heading', { level: 2 }),
        isBulletList: snapshot.editor.isActive('bulletList'),
        isOrderedList: snapshot.editor.isActive('orderedList'),
        canUndo: snapshot.editor.can().chain().undo().run(),
        canRedo: snapshot.editor.can().chain().redo().run(),
      };
    },
  });

  // Memoize callback handlers để tránh tạo function mới mỗi render
  // Hooks phải được gọi TRƯỚC early return
  const toggleBold = useCallback(() => editor?.chain().focus().toggleBold().run(), [editor]);
  const toggleItalic = useCallback(() => editor?.chain().focus().toggleItalic().run(), [editor]);
  const toggleCode = useCallback(() => editor?.chain().focus().toggleCode().run(), [editor]);
  const toggleCodeBlock = useCallback(() => editor?.chain().focus().toggleCodeBlock().run(), [editor]);
  const toggleBulletList = useCallback(() => editor?.chain().focus().toggleBulletList().run(), [editor]);
  const toggleOrderedList = useCallback(() => editor?.chain().focus().toggleOrderedList().run(), [editor]);
  const toggleHeading1 = useCallback(() => editor?.chain().focus().toggleHeading({ level: 1 }).run(), [editor]);
  const toggleHeading2 = useCallback(() => editor?.chain().focus().toggleHeading({ level: 2 }).run(), [editor]);
  const undo = useCallback(() => editor?.chain().focus().undo().run(), [editor]);
  const redo = useCallback(() => editor?.chain().focus().redo().run(), [editor]);

  const editorContentStyle = React.useMemo(() => ({
    ...STYLES.editorContent,
    minHeight: minHeight,
  }), [minHeight]);

  if (!editor || !editorState) {
    return <div style={{ padding: '16px', color: '#999999' }}>Loading editor...</div>;
  }

  const state = editorState;

  return (
    <div style={STYLES.container}>
      {label && (
        <label style={STYLES.label}>
          {label}
        </label>
      )}
      <div
        style={{
          ...STYLES.editorWrapper,
          ...(error ? STYLES.editorWrapperError : STYLES.editorWrapperNormal),
        }}
      >
        {/* Toolbar */}
        <Toolbar
          state={state}
          onToggleBold={toggleBold}
          onToggleItalic={toggleItalic}
          onToggleCode={toggleCode}
          onToggleCodeBlock={toggleCodeBlock}
          onToggleBulletList={toggleBulletList}
          onToggleOrderedList={toggleOrderedList}
          onToggleHeading1={toggleHeading1}
          onToggleHeading2={toggleHeading2}
          onUndo={undo}
          onRedo={redo}
        />

        {/* Editor Area */}
        <div
          className={EDITOR_CLASS_NAME}
          style={editorContentStyle}
        >
          {editor && <EditorContent editor={editor} />}
        </div>
      </div>
      {helperText && (
        <div style={{
          ...STYLES.helperText,
          color: error ? '#D32F2F' : '#666666',
        }}>
          {helperText}
        </div>
      )}
    </div>
  );
};

export default React.memo(RichEditor);
